import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Autoplay } from "swiper/modules";
import { Box, Typography, Rating, IconButton } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { get_all_testimonials } from '../../services/testimonialService.js'
import colors from "../../components/colors.js";

export default function TestimonialSlider({ restaurant }) {
  const themeColors = {
    accent: restaurant?.theme_config?.accent_color || '#FF6B35',
    primary: restaurant?.theme_config?.primary_color || '#D4AF37',
    secondary: restaurant?.theme_config?.secondary_color || '#8B4513',
  };

  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    try {
      async function getTestimonials() {
        const response = await get_all_testimonials();
        setTestimonials(response)
      }
      getTestimonials()
    }
    catch (error) {
      console.error("Error on Getting Testimonials")
    }
  }, [])

  // console.log("Testimonials => ", testimonials)

  return (
    <>
      {testimonials.length > 0 && (
        <>
          <Box sx={{ paddingTop: { xs: 4, md: 6 }, backgroundColor: '#FFFFFF', textAlign: 'center' }}>
            <Typography sx={{
              fontWeight: "600 !important",
              color: `${colors.primary.main} !important`,
              textTransform: "uppercase",
              fontSize: {
                xs: "16px !important",
                sm: "24px !important",
                md: "32px !important",
              },
              mb: 1,
              fontFamily: 'playfair Display !important',
              letterSpacing: "1px !important",
            }}>What Our Customers Say</Typography>
            <Typography variant="h6"
              sx={{
                color: `${colors.primary.main} !important`,
                fontSize: {
                  xs: "12px !important",
                  sm: "16px !important",
                  md: "18px !important",
                },
                fontFamily: "Inter, sans-serif !important",
                fontWeight: "400 !important",
                maxWidth: "600px",
                mx: "auto",
              }}
            >Real stories from food lovers who've enjoyed our hospitality</Typography>
          </Box>

          <Box
            sx={{
              maxWidth: '1440px',
              margin: { md: '0 auto' },
              height: { xs: '40vh', sm: '40vh', md: "350px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              mb: 2,
              backgroundColor: "#FFFFFF",
              position: "relative",
            }}
          >
            <Swiper
              modules={[Navigation, Autoplay]}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop={true}
              style={{
                width: "80%",
                height: "100%",
              }}
            >
              {testimonials.map((t, index) => (
                <SwiperSlide key={index}>
                  <Box sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 2,
                  }}>
                    <Rating
                      name={`testimonial-rating-${index}`}
                      defaultValue={t.stars}
                      precision={0.5}
                      readOnly
                      sx={{
                        mb: 2,
                        "& .MuiSvgIcon-root": { fontSize: { xs: '15px !important', sm: '15px !important', md: "25px !important" } },
                        "& .MuiRating-iconEmpty": {
                          color: "transparent",
                          WebkitTextStroke: "2px #FFCF03",
                        },
                        "& .MuiRating-iconFilled": {
                          color: "#FFCF03",
                          WebkitTextStroke: "2px #FFCF03",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: '15px !important', sm: '15px !important', md: "20px !important" },
                        color: colors.primary.main,
                        mb: 2,
                        fontFamily: "Inter, sans-serif !important",
                        fontWeight: "500 !important",
                      }}
                    >
                      "{t.title}"
                    </Typography>
                    <Typography
                      sx={{
                        margin: "0 auto",
                        width: { xs: "90%", md: "50%" },
                        fontSize: { xs: '12px !important', sm: '12px !important', md: "16px !important" },
                        color: "#1A1A1A",
                        fontFamily: "Inter, sans-serif !important",
                        fontWeight: "400 !important",
                        lineHeight: "1.6 !important",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.description}
                    </Typography>
                    <Typography
                      sx={{
                        margin: "0 auto",
                        width: { xs: "90%", md: "50%" },
                        fontSize: { xs: '12px !important', sm: '12px !important', md: "16px !important" },
                        color: colors.primary.main,
                        fontFamily: "Inter, sans-serif !important",
                        fontWeight: "600 !important",
                        mt: 2,
                      }}
                    >
                      - {t.customer_name}
                    </Typography>
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Navigation Buttons */}
            <IconButton
              className="swiper-button-prev"
              sx={{
                position: "absolute",
                left: { xs: 10, sm: 20, md: 50 },
                zIndex: 10,
                backgroundColor: colors.primary.main,
                color: "white",
                width: { xs: 40, md: 50 },
                height: { xs: 40, md: 50 },
                "&:hover": {
                  backgroundColor: colors.secondary.main,
                },
                "&::after": {
                  display: "none",
                },
              }}
            >
              <ArrowBackIos sx={{ fontSize: { xs: 20, md: 24 } }} />
            </IconButton>

            <IconButton
              className="swiper-button-next"
              sx={{
                position: "absolute",
                right: { xs: 10, sm: 20, md: 50 },
                zIndex: 10,
                backgroundColor: colors.primary.main,
                color: "white",
                width: { xs: 40, md: 50 },
                height: { xs: 40, md: 50 },
                "&:hover": {
                  backgroundColor: colors.secondary.main,
                },
                "&::after": {
                  display: "none",
                },
              }}
            >
              <ArrowForwardIos sx={{ fontSize: { xs: 20, md: 24 } }} />
            </IconButton>
          </Box>
        </>
      )}
    </>
  );
}